import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Sparkles, Linkedin, Presentation, Copy, Check, Loader2,
  History as HistoryIcon, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'linkedin', label: 'Post LinkedIn', icon: Linkedin },
  { key: 'pitch', label: 'Pitch Deck', icon: Presentation },
];

const Studio = () => {
  const { api } = useAuth();
  const [tab, setTab] = useState('linkedin');
  const [options, setOptions] = useState({ linkedin_formats: [], linkedin_tones: [], pitch_audiences: [] });

  // LinkedIn form
  const [liFormat, setLiFormat] = useState('storytelling');
  const [liTone, setLiTone] = useState('founder');
  const [liTopic, setLiTopic] = useState('');
  const [liAudience, setLiAudience] = useState('');

  // Pitch form
  const [pitchAudience, setPitchAudience] = useState('investors');
  const [pitchDuration, setPitchDuration] = useState(10);

  // Output
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const outputRef = useRef(null);

  useEffect(() => {
    api.get('/api/studio/options').then(r => setOptions(r.data || {})).catch(() => {});
    refreshHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshHistory = async () => {
    try {
      const r = await api.get('/api/studio/history');
      setHistory(r.data?.items || []);
    } catch { /* noop */ }
  };

  const generate = async () => {
    if (tab === 'linkedin' && (!liTopic || liTopic.trim().length < 5)) {
      toast.error('Décris le sujet du post (min. 5 caractères)');
      return;
    }
    setGenerating(true);
    setOutput('');
    setCopied(false);
    try {
      const url = tab === 'linkedin' ? '/api/studio/linkedin' : '/api/studio/pitch-deck';
      const payload = tab === 'linkedin'
        ? { format: liFormat, tone: liTone, topic: liTopic.trim(), target_audience: liAudience.trim() || null }
        : { audience: pitchAudience, duration_min: pitchDuration };
      const r = await api.post(url, payload, { timeout: 110000 });
      setOutput(r.data?.content || '');
      refreshHistory();
      // Scroll output into view
      setTimeout(() => { outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Génération impossible — réessaie dans un instant');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      toast.success('Copié dans le presse-papiers');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const loadHistoryItem = async (id) => {
    try {
      const r = await api.get(`/api/studio/history/${id}`);
      setOutput(r.data?.content || '');
      setTimeout(() => { outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    } catch { /* noop */ }
  };

  return (
    <div data-testid="studio-page" className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-6 h-6 text-atlasCyan" />
            <h1 className="text-3xl font-bold text-white">Studio</h1>
          </div>
          <p className="text-atlasTextSecondary">
            Génère du contenu marketing premium qui utilise ta mémoire ATLAS et ton expertise sectorielle.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-atlasBorder">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`studio-tab-${t.key}`}
              onClick={() => { setTab(t.key); setOutput(''); }}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
                tab === t.key
                  ? 'text-atlasCyan border-atlasCyan'
                  : 'text-atlasTextSecondary border-transparent hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Form */}
      <div className="glass-card p-6 space-y-4">
        {tab === 'linkedin' ? (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-atlasTextSecondary text-sm">Format</Label>
                <select
                  data-testid="li-format-select"
                  value={liFormat}
                  onChange={e => setLiFormat(e.target.value)}
                  className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white"
                >
                  {options.linkedin_formats.map(f => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-atlasTextSecondary text-sm">Ton</Label>
                <select
                  data-testid="li-tone-select"
                  value={liTone}
                  onChange={e => setLiTone(e.target.value)}
                  className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white"
                >
                  <option value="founder">Founder authentique</option>
                  <option value="expert">Expert sectoriel</option>
                  <option value="provocateur">Provocateur respectueux</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-atlasTextSecondary text-sm">Sujet du post</Label>
              <Textarea
                data-testid="li-topic-input"
                value={liTopic}
                onChange={e => setLiTopic(e.target.value)}
                placeholder="Ex: Mon premier mois en tant que CEO, ce que je n'avais pas vu venir"
                className="input-atlas mt-1 min-h-[80px]"
              />
            </div>
            <div>
              <Label className="text-atlasTextSecondary text-sm">Audience cible (optionnel)</Label>
              <Input
                data-testid="li-audience-input"
                value={liAudience}
                onChange={e => setLiAudience(e.target.value)}
                placeholder="Ex: CEO PME 1-10 employés, restaurateurs"
                className="input-atlas mt-1"
              />
            </div>
          </>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-atlasTextSecondary text-sm">Audience</Label>
              <select
                data-testid="pitch-audience-select"
                value={pitchAudience}
                onChange={e => setPitchAudience(e.target.value)}
                className="input-atlas mt-1 w-full px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white"
              >
                {options.pitch_audiences.map(a => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-atlasTextSecondary text-sm">Durée présentation (minutes)</Label>
              <Input
                data-testid="pitch-duration-input"
                type="number"
                min="5" max="20"
                value={pitchDuration}
                onChange={e => setPitchDuration(parseInt(e.target.value) || 10)}
                className="input-atlas mt-1"
              />
              <p className="text-[11px] text-atlasTextSecondary mt-1">≈ 1 slide par minute · 8-15 recommandé</p>
            </div>
          </div>
        )}

        <Button
          data-testid="studio-generate-btn"
          onClick={generate}
          disabled={generating}
          className="btn-primary w-full sm:w-auto"
        >
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Génération en cours…</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" />Générer</>
          )}
        </Button>

        {tab === 'pitch' && generating && (
          <p className="text-xs text-atlasTextSecondary italic">
            La génération d'un pitch deck complet prend ~30-60 secondes. ATLAS rédige les speaker notes pour chaque slide.
          </p>
        )}
      </div>

      {/* Output */}
      {output && (
        <div ref={outputRef} className="glass-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Résultat</h2>
            <div className="flex gap-2">
              <button
                data-testid="studio-regenerate-btn"
                onClick={generate}
                disabled={generating}
                className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md text-atlasTextSecondary hover:text-white hover:bg-white/5 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Regénérer
              </button>
              <button
                data-testid="studio-copy-btn"
                onClick={copyToClipboard}
                className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-md bg-atlasCyan/10 text-atlasCyan hover:bg-atlasCyan/20 transition-colors"
              >
                {copied ? <><Check className="w-3.5 h-3.5" />Copié</> : <><Copy className="w-3.5 h-3.5" />Copier</>}
              </button>
            </div>
          </div>
          <pre
            data-testid="studio-output"
            className="text-white text-sm whitespace-pre-wrap font-sans leading-relaxed bg-atlasBg/50 p-4 rounded-lg border border-atlasBorder overflow-x-auto"
          >
            {output}
          </pre>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <HistoryIcon className="w-4 h-4 text-atlasTextSecondary" />
            <h3 className="text-sm font-semibold text-white">Historique récent</h3>
          </div>
          <ul className="space-y-2">
            {history.slice(0, 8).map(h => (
              <li key={h.id}>
                <button
                  data-testid={`studio-history-${h.id}`}
                  onClick={() => loadHistoryItem(h.id)}
                  className="w-full flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-atlasBorder hover:border-atlasCyan/40 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {h.kind === 'linkedin' ? (
                      <Linkedin className="w-4 h-4 text-atlasCyan flex-shrink-0" />
                    ) : (
                      <Presentation className="w-4 h-4 text-atlasCyan flex-shrink-0" />
                    )}
                    <span className="text-white text-sm truncate">
                      {h.kind === 'linkedin' ? (h.topic || h.format) : (h.audience ? `Pitch — ${h.audience}` : 'Pitch deck')}
                    </span>
                  </div>
                  <span className="text-[11px] text-atlasTextSecondary atlas-mono whitespace-nowrap">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Studio;
